import React, { useState } from 'react';
import { validatePlannedExpense } from '@/utils/validators';
import { toISODate } from '@/utils/formatters';
import { useAppContext } from '@/context/AppContext';

const EMPTY = {
  date:        toISODate(),
  vendor:      '',
  amount:      '',
  description: '',
  label:       '',
};

export default function AddPlannedForm({ onSave, onClose, initialData = null }) {
  const { plannedLabels } = useAppContext();
  const [form, setForm]   = useState(initialData ? {
    date:        initialData.date,
    vendor:      initialData.vendor ?? '',
    amount:      String(initialData.amount),
    description: initialData.description ?? '',
    label:       initialData.label ?? '',
  } : EMPTY);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initialData;

  function set(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    const { valid, errors: errs } = validatePlannedExpense(form);
    if (!valid) { setErrors(errs); return; }
    setSaving(true);
    try {
      await onSave({ ...form, amount: Number(form.amount) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl p-5 space-y-4 slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-bold text-gray-800">{isEdit ? 'Edit Planned Expense' : 'Add Planned Expense'}</h2>
          <button onClick={onClose} className="text-gray-400 text-lg">✕</button>
        </div>

        {errors.length > 0 && (
          <div className="bg-red-50 rounded p-2">
            {errors.map(e => <p key={e} className="text-xs text-danger">• {e}</p>)}
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date *</label>
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className="input-field" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vendor / Payee *</label>
          <input type="text" value={form.vendor} onChange={e => set('vendor', e.target.value)}
            placeholder="e.g. Bhadra Travel, DPS School" className="input-field" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Amount (₹) *</label>
          <div className="flex items-center gap-2 border border-gray-300 rounded-input px-3 py-2.5">
            <span className="text-warning font-bold">₹</span>
            <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)}
              placeholder="0" className="flex-1 outline-none text-lg font-bold text-gray-800" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description *</label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)}
            placeholder="e.g. Family trip to Ooty — April 2026"
            rows={2} className="input-field resize-none" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Label *</label>
          <div className="flex flex-wrap gap-2">
            {plannedLabels.map(l => (
              <button key={l.id} type="button"
                onClick={() => set('label', l.id)}
                className={`chip ${form.label === l.id ? 'chip-selected' : ''}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="btn-primary w-full py-3 disabled:opacity-60">
          {saving ? 'Saving…' : `✓ ${isEdit ? 'Update' : 'Add'} Planned Expense`}
        </button>
      </div>
    </div>
  );
}
